import React, { useState, useCallback } from "react";
import {
  Modal,
  Button,
  Tabs,
  Input,
  Upload,
  Table,
  Space,
  Typography,
  Alert,
  Form,
  Select,
  Divider,
  Row,
  Col,
  Progress,
  Tag,
} from "antd";
import {
  UploadOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd";
import Papa from "papaparse";
import {
  BulkUserDto,
  BulkCreateUsersDto,
  BulkValidateUsersDto,
  BulkValidationResponse,
  DuplicatedEmail,
} from "../../../../services/schools/school.types";
import { bulkValidateSchoolUsers } from "../../../../services/schools/school.service";
import {
  buildConflictNotice,
  buildValidationSummaryText,
  countUsers,
  groupUsersByCampus,
  mergeValidationResults,
  parseBulkRow,
  resolveRowCampus,
  splitDuplicateConflicts,
} from "./BulkUploadModal.utils";
import { CampusRef } from "../../../../services/schools/campus.types";
import {
  ALL_CAMPUSES,
  campusOptions,
  hasMultipleCampuses,
} from "./UsersTab.utils";
import { useGrades } from "../../../../hooks/useGrades";
import { ClassEntity } from "../types";
import { uniqueClassrooms } from "./UsersTab.utils";
import "./BulkUploadModal.css";
import { useNotification } from "../../../../hooks/useNotification";

interface BulkUploadModalProps {
  visible: boolean;
  onClose: () => void;
  onUpload: (data: BulkCreateUsersDto) => Promise<void>;
  schoolId: number;
  classes: ClassEntity[];
  /** SCRUM-916: árbol de planteles; con uno solo el modal se ve como antes. */
  campuses?: CampusRef[];
}

interface PreviewUser extends BulkUserDto {
  _index: number;
  _error?: string;
  _status?: "valid" | "error";
  /** Plantel destino ya resuelto: es el `school_id` con el que se sube. */
  campus_id?: number;
}

const { TabPane } = Tabs;
const { Title, Text } = Typography;

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  visible,
  onClose,
  onUpload,
  schoolId,
  classes,
  campuses = [],
}) => {
  const { grades } = useGrades();
  const [loading, setLoading] = useState(false);
  const [previewUsers, setPreviewUsers] = useState<PreviewUser[]>([]);
  const [activeTab, setActiveTab] = useState("csv");
  const [googleSheetUrl, setGoogleSheetUrl] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [targetCampusId, setTargetCampusId] = useState<number>(ALL_CAMPUSES);
  const showCampusPicker = hasMultipleCampuses(campuses);

  const { notify } = useNotification();

  // Nuevos estados para validación y conflictos
  const [validationResult, setValidationResult] =
    useState<BulkValidationResponse | null>(null);
  const [processingStep, setProcessingStep] = useState<
    "preview" | "validation" | "conflicts" | "upload"
  >("preview");

  // Estados para barra de progreso
  const [uploadProgress, setUploadProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState<
    "active" | "success" | "exception"
  >("active");
  const [progressText, setProgressText] = useState("");

  // Función para validar un usuario
  const validateUser = useCallback(
    (user: any, index: number): PreviewUser => {
      const errors: string[] = [];

      // Validar campos requeridos
      if (!user.first_name || user.first_name.trim().length < 2) {
        errors.push("Nombre debe tener al menos 2 caracteres");
      }
      if (!user.last_name || user.last_name.trim().length < 2) {
        errors.push("Apellido debe tener al menos 2 caracteres");
      }
      if (!user.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) {
        errors.push("Email inválido");
      }
      if (!user.role || !["TEACHER", "STUDENT"].includes(user.role)) {
        errors.push("Rol debe ser TEACHER o STUDENT");
      }

      // Validar grades según el rol
      if (user.grades && Array.isArray(user.grades) && user.grades.length > 0) {
        // Validar que los grados existan
        const invalidGrades = user.grades.filter(
          (gradeId: any) => !grades.find((g: any) => g.id === gradeId)
        );
        if (invalidGrades.length > 0) {
          errors.push(`Grados inválidos: ${invalidGrades.join(", ")}`);
        }

        // NUEVA VALIDACIÓN: Estudiantes solo pueden tener UN grado
        if (user.role === "STUDENT" && user.grades.length > 1) {
          errors.push("Los estudiantes solo pueden tener UN grado asignado");
        }
      }

      // Validar classroom_codes
      if (
        user.classroom_codes &&
        Array.isArray(user.classroom_codes) &&
        user.classroom_codes.length > 0
      ) {
        // NUEVA VALIDACIÓN: No códigos duplicados
        const uniqueCodes = new Set(user.classroom_codes);
        if (uniqueCodes.size !== user.classroom_codes.length) {
          const duplicates = user.classroom_codes.filter(
            (code: string, index: number) =>
              user.classroom_codes.indexOf(code) !== index
          );
          errors.push(
            `Códigos de clase duplicados: ${Array.from(
              new Set(duplicates)
            ).join(", ")}`
          );
        }

        // Validar que los códigos existan
        const invalidCodes = user.classroom_codes.filter(
          (code: any) =>
            !classes.find(
              (c) => c.custom_code === code || c.class_code === code
            )
        );
        if (invalidCodes.length > 0) {
          errors.push(
            `Códigos de clase no encontrados: ${invalidCodes.join(", ")}`
          );
        }

        // NUEVA VALIDACIÓN: Coherencia entre grados y clases
        if (
          user.grades &&
          Array.isArray(user.grades) &&
          user.grades.length > 0
        ) {
          const classGrades = new Set();
          user.classroom_codes.forEach((code: string) => {
            const classItem = classes.find(
              (c) => c.custom_code === code || c.class_code === code
            );
            if (classItem && classItem.grade_id) {
              classGrades.add(classItem.grade_id);
            }
          });

          const userGradesSet = new Set(user.grades);
          const missingGrades = Array.from(classGrades).filter(
            (grade) => !userGradesSet.has(grade)
          );
          const extraGrades = user.grades.filter(
            (grade: number) => !classGrades.has(grade)
          );

          if (missingGrades.length > 0 || extraGrades.length > 0) {
            // Convertir IDs de grados a códigos de grados para mostrar en el error
            const userGradeCodes = user.grades.map((gradeId: number) => {
              const grade = grades.find((g: any) => g.id === gradeId);
              return grade ? grade.grade : `ID:${gradeId}`;
            });

            const classGradeCodes = Array.from(classGrades).map(
              (gradeId: any) => {
                const grade = grades.find((g: any) => g.id === gradeId);
                return grade ? grade.grade : `ID:${gradeId}`;
              }
            );

            errors.push(
              `Los grados asignados [${userGradeCodes.join(
                ", "
              )}] no coinciden con los grados de las clases seleccionadas [${classGradeCodes.join(
                ", "
              )}]`
            );
          }
        }
      }

      // SCRUM-916: el plantel de la fila (columna Plantel) manda sobre el
      // seleccionado en pantalla. En una escuela multi-plantel sin ninguno de
      // los dos la fila se marca con error: nunca se asigna a la raíz por
      // descarte (R6.3).
      const resolvedCampus = resolveRowCampus(
        user.campus ?? "",
        campuses,
        targetCampusId === ALL_CAMPUSES ? undefined : targetCampusId
      );
      if (resolvedCampus.error) errors.push(resolvedCampus.error);

      return {
        ...user,
        first_name: user.first_name?.trim() || "",
        last_name: user.last_name?.trim() || "",
        email: user.email?.trim() || "",
        role: user.role || "STUDENT",
        grades: user.grades || [],
        classroom_codes: user.classroom_codes || [],
        campus_id: resolvedCampus.campusId,
        _index: index,
        _error: errors.length > 0 ? errors.join("; ") : undefined,
        _status: errors.length > 0 ? "error" : "valid",
      };
    },
    [grades, classes, campuses, targetCampusId]
  );

  // Función para procesar archivo CSV
  const handleCSVUpload = useCallback(
    (file: File) => {
      Papa.parse(file, {
        header: true,
        complete: (results: any) => {
          const users = results.data
            .filter((row: any) => row.first_name || row.last_name || row.email)
            .map((row: any, index: number) =>
              validateUser(parseBulkRow(row), index)
            );

          setPreviewUsers(users);
        },
        error: (error: any) => {
          console.error("Error parsing CSV:", error);
        },
      });
    },
    [validateUser]
  );

  // Función para procesar Google Sheets
  const handleGoogleSheetLoad = useCallback(async () => {
    if (!googleSheetUrl.trim()) return;

    setLoading(true);
    try {
      // Convertir URL de Google Sheets a formato CSV
      let csvUrl = googleSheetUrl.trim();

      // Extraer el ID del documento de la URL
      const spreadsheetIdMatch = csvUrl.match(
        /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/
      );
      if (!spreadsheetIdMatch) {
        throw new Error("URL de Google Sheets inválida");
      }

      const spreadsheetId = spreadsheetIdMatch[1];

      // Extraer el GID si está presente
      let gid = "0"; // GID por defecto
      const gidMatch = csvUrl.match(/[#&?]gid=([0-9]+)/);
      if (gidMatch) {
        gid = gidMatch[1];
      }

      // Construir la URL de exportación CSV correcta
      csvUrl = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/export?format=csv&gid=${gid}`;

      const response = await fetch(csvUrl);

      if (!response.ok) {
        throw new Error(
          `Error ${response.status}: ${response.statusText}. Verifica que la hoja de Google Sheets sea pública.`
        );
      }

      const csvText = await response.text();

      Papa.parse(csvText, {
        header: true,
        complete: (results: any) => {
          const users = results.data
            .filter((row: any) => row.first_name || row.last_name || row.email)
            .map((row: any, index: number) =>
              validateUser(parseBulkRow(row), index)
            );

          setPreviewUsers(users);
        },
        error: (error: any) => {
          console.error("Error parsing Google Sheets CSV:", error);
        },
      });
    } catch (error) {
      console.error("Error fetching Google Sheets:", error);
      // Mostrar mensaje de error al usuario
      notify(
        "error",
        "Error",
        `Error cargando Google Sheets: ${
          error instanceof Error ? error.message : "Error desconocido"
        }`
      );
    } finally {
      setLoading(false);
    }
  }, [googleSheetUrl, validateUser]);

  // Props para Upload de Antd
  const uploadProps: UploadProps = {
    accept: ".csv",
    beforeUpload: (file) => {
      handleCSVUpload(file);
      return false; // Prevenir upload automático
    },
    fileList,
    onChange: ({ fileList: newFileList }) => setFileList(newFileList),
    maxCount: 1,
  };

  // Función para validar usuarios con el backend antes del upload
  const handleValidateUsers = async () => {
    const validUsers = previewUsers.filter((user) => user._status === "valid");
    if (validUsers.length === 0) return;

    setLoading(true);
    setProcessingStep("validation");
    setUploadProgress(0);
    setProgressStatus("active");
    setProgressText("Iniciando validación...");

    try {
      // Simular progreso inicial
      setUploadProgress(10);
      setProgressText("Preparando datos para validación...");

      // Remover campos de preview antes de enviar
      const usersToValidate = validUsers.map(
        ({ _index, _error, _status, ...user }) => user
      );

      setUploadProgress(30);
      setProgressText(
        `Validando ${countUsers(validUsers.length)} en el servidor...`
      );
      // SCRUM-916: el endpoint recibe UN school_id y un plantel ES una fila de
      // School, así que cada plantel destino se valida contra sí mismo. Sin
      // planteles hay un solo grupo y esto es la llamada de siempre.
      const validationGroups = groupUsersByCampus(usersToValidate, schoolId);
      const result = mergeValidationResults(
        await Promise.all(
          Array.from(validationGroups.entries()).map(
            ([targetSchoolId, users]) => {
              const validationData: BulkValidateUsersDto = {
                school_id: targetSchoolId,
                users: users.map(({ campus_id, ...user }) => user),
                ...(targetSchoolId !== schoolId
                  ? { campus_id: targetSchoolId }
                  : {}),
              };
              return bulkValidateSchoolUsers(validationData);
            }
          )
        )
      );

      setUploadProgress(80);
      setProgressText("Procesando resultados de validación...");

      if (result) {
        setValidationResult(result);

        setUploadProgress(100);
        setProgressStatus("success");

        if (result.hasConflicts) {
          setProgressText(
            `Validación completada - ${result.duplicatedEmails.length} ${
              result.duplicatedEmails.length === 1
                ? "conflicto detectado"
                : "conflictos detectados"
            }`
          );
          setProcessingStep("conflicts");
        } else {
          setProgressText("Validación exitosa - Sin conflictos detectados");
          // Si no hay conflictos, proceder directamente con la creación
          await handleBulkUpload(false, true); // updateExisting=false, skipValidation=true
        }
      } else {
        console.error("❌ El resultado de validación es null o undefined");
        setProgressStatus("exception");
        setProgressText("Error en la validación - Resultado inválido");
      }
    } catch (error) {
      console.error("❌ Error validating users:", error);
      setProgressStatus("exception");
      setProgressText(
        `Error en la validación: ${
          error instanceof Error ? error.message : "Error desconocido"
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar la subida masiva (actualizada)
  const handleBulkUpload = async (
    updateExisting: boolean = false,
    skipValidation: boolean = false
  ) => {
    const validUsers = previewUsers.filter((user) => user._status === "valid");
    if (validUsers.length === 0) return;

    setLoading(true);
    setProcessingStep("upload");
    setUploadProgress(0);
    setProgressStatus("active");
    setProgressText("Iniciando carga de usuarios...");

    try {
      // Simular progreso inicial
      setUploadProgress(10);
      setProgressText("Preparando datos para envío...");

      // Remover campos de preview antes de enviar
      const usersToUpload = validUsers.map(
        ({ _index, _error, _status, ...user }) => user
      );

      setUploadProgress(30);
      setProgressText(`Enviando ${countUsers(validUsers.length)} al servidor...`);

      const uploadGroups = groupUsersByCampus(usersToUpload, schoolId);
      for (const [targetSchoolId, users] of Array.from(
        uploadGroups.entries()
      )) {
        const bulkData: BulkCreateUsersDto = {
          school_id: targetSchoolId,
          users: users.map(({ campus_id, ...user }) => user),
          updateExisting,
          skipValidation,
          ...(targetSchoolId !== schoolId
            ? { campus_id: targetSchoolId }
            : {}),
        };
        await onUpload(bulkData);
      }

      setUploadProgress(90);
      setProgressText("Finalizando proceso...");

      // Simular un pequeño delay para mostrar el progreso completo
      setTimeout(() => {
        setUploadProgress(100);
        setProgressStatus("success");
        setProgressText("¡Carga completada exitosamente!");

        // Cerrar modal después de un momento
        setTimeout(() => {
          handleClose();
        }, 1500);
      }, 500);
    } catch (error) {
      console.error("Error uploading users:", error);
      setProgressStatus("exception");
      setProgressText(
        `Error en la carga: ${
          error instanceof Error ? error.message : "Error desconocido"
        }`
      );
      setLoading(false);
    }
  };

  // Función para limpiar estado y cerrar modal
  const handleClose = () => {
    setPreviewUsers([]);
    setGoogleSheetUrl("");
    setFileList([]);
    setActiveTab("csv");
    setValidationResult(null);
    setProcessingStep("preview");
    setUploadProgress(0);
    setProgressStatus("active");
    setProgressText("");
    setTargetCampusId(ALL_CAMPUSES);
    onClose();
  };

  // Función para descargar template CSV
  const downloadTemplate = () => {
    // SCRUM-916: la columna sólo se documenta cuando la escuela tiene
    // planteles; en las 147 restantes el template queda idéntico.
    const campusExample = (index: number) =>
      showCampusPicker
        ? { plantel: campuses[index % campuses.length].abbreviation ?? "" }
        : {};
    const template = [
      {
        first_name: "María",
        last_name: "García",
        email: "maria.garcia@example.com",
        role: "STUDENT",
        grades: "1", // ESTUDIANTES: Solo UN grado
        classroom_codes: "ADM_K2_LECK2_Test,ADM_P2_MATP2_Marquitos", // Máximo 4 clases para ESTUDIANTES, sin límite para PROFESORES
        ...campusExample(0),
      },
      {
        first_name: "Juan",
        last_name: "Pérez",
        email: "juan.perez@example.com",
        role: "TEACHER",
        grades: "1,2", // PROFESORES: Pueden tener múltiples grados
        classroom_codes: "ADM_K2_LECK2_Test", // Clases que enseña
        ...campusExample(1),
      },
      {
        first_name: "Ana",
        last_name: "Rodríguez",
        email: "ana.rodriguez@example.com",
        role: "STUDENT",
        grades: "2", // ESTUDIANTES: Solo UN grado
        classroom_codes: "ADM_P2_MATP2_Marquitos", // Clase correspondiente al grado
        ...campusExample(2),
      },
      {
        first_name: "Carlos",
        last_name: "López",
        email: "carlos.lopez@example.com",
        role: "TEACHER",
        grades: "1,2", // PROFESORES: Múltiples grados permitidos
        classroom_codes: "ADM_K2_LECK2_Test,ADM_P2_MATP2_Marquitos", // Clases de ambos grados
        ...campusExample(3),
      },
    ];

    // Agregar comentarios explicativos al inicio del CSV
    const csvWithComments = [
      "# TEMPLATE DE CARGA MASIVA DE USUARIOS - EMMI",
      "# IMPORTANTE: Elimina estas líneas de comentarios antes de subir el archivo",
      "# ",
      "# PROCESO:",
      "# 1. Completa los datos de los usuarios en las filas siguientes",
      "# 2. Sube el archivo en la plataforma",
      "# 3. El sistema validará automáticamente y detectará emails duplicados",
      "# 4. Confirma si quieres crear solo nuevos usuarios o actualizar existentes",
      "# ",
      "# REGLAS DE VALIDACIÓN:",
      "# - ESTUDIANTES: Solo pueden tener UN grado asignado",
      "# - PROFESORES: Pueden tener múltiples grados asignados",
      "# - ESTUDIANTES: Solo pueden tener UN grado y máximo 4 clases",
      "# - PROFESORES: Pueden tener múltiples grados y clases sin límite",
      "# - No duplicar códigos de clases para el mismo usuario",
      "# - Los grados deben coincidir con las clases seleccionadas",
      "# - Emails duplicados serán detectados y requerirán confirmación",
      ...(showCampusPicker
        ? [
            "# - Plantel: opcional; abreviatura o nombre del plantel. Si se omite se usa el plantel seleccionado en pantalla",
          ]
        : []),
      "# ",
      Papa.unparse(template),
    ].join("\n");

    const blob = new Blob([csvWithComments], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "template_usuarios.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Columnas para la tabla de preview
  const previewColumns = [
    {
      title: "#",
      dataIndex: "_index",
      key: "_index",
      width: 50,
      render: (index: number) => index + 1,
    },
    {
      title: "Estado",
      dataIndex: "_status",
      key: "_status",
      width: 80,
      render: (status: string) =>
        status === "valid" ? (
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
        ) : (
          <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
        ),
    },
    {
      title: "Nombre",
      dataIndex: "first_name",
      key: "first_name",
      width: 150,
      sorter: (a: PreviewUser, b: PreviewUser) =>
        a.first_name.localeCompare(b.first_name),
    },
    {
      title: "Apellido",
      dataIndex: "last_name",
      key: "last_name",
      width: 150,
      sorter: (a: PreviewUser, b: PreviewUser) =>
        a.last_name.localeCompare(b.last_name),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 250,
      sorter: (a: PreviewUser, b: PreviewUser) =>
        a.email.localeCompare(b.email),
    },
    {
      title: "Rol",
      dataIndex: "role",
      key: "role",
      width: 100,
      render: (role: string) => {
        const roleLabels = {
          TEACHER: "Profesor",
          STUDENT: "Estudiante",
        };
        return roleLabels[role as keyof typeof roleLabels] || role;
      },
    },
    {
      title: "Grados",
      dataIndex: "grades",
      key: "grades",
      width: 120,
      sorter: (a: PreviewUser, b: PreviewUser) => {
        const aGrades = a.grades || [];
        const bGrades = b.grades || [];
        if (aGrades.length === 0 && bGrades.length === 0) return 0;
        if (aGrades.length === 0) return 1;
        if (bGrades.length === 0) return -1;
        return aGrades[0] - bGrades[0]; // Ordenar por el primer grado
      },
      render: (gradeIds: number[]) => {
        if (!gradeIds || gradeIds.length === 0) return "Sin asignar";
        const gradeNames = gradeIds.map((id) => {
          const grade = grades.find((g: any) => g.id === id);
          return grade ? grade.grade : `ID:${id}`;
        });
        return gradeNames.join(", ");
      },
    },
    ...(showCampusPicker
      ? [
          {
            title: "Plantel",
            dataIndex: "campus_id",
            key: "campus_id",
            width: 140,
            render: (campusId: number | undefined) =>
              campuses.find((campus) => campus.id === campusId)?.name ?? "—",
          },
        ]
      : []),
    {
      title: "Códigos de Clases",
      dataIndex: "classroom_codes",
      key: "classroom_codes",
      width: 200,
      render: (classCodes: string[]) => {
        if (!classCodes || classCodes.length === 0) return "Sin asignar";
        return classCodes.join(", ");
      },
    },
    {
      title: "Error",
      dataIndex: "_error",
      key: "_error",
      render: (error: string) =>
        error ? <Text type="danger">{error}</Text> : null,
    },
  ];

  // SCRUM-822: split the duplicate report before rendering, so both the table
  // and the action buttons agree on what can actually be updated.
  const { updatable: updatableConflicts, blocked: blockedConflicts } =
    splitDuplicateConflicts(validationResult);

  const validCount = previewUsers.filter(
    (user) => user._status === "valid"
  ).length;
  const errorCount = previewUsers.filter(
    (user) => user._status === "error"
  ).length;

  // Componente para mostrar conflictos de validación
  const ConflictsTable = () => {
    if (
      !validationResult ||
      !validationResult.hasConflicts ||
      !validationResult.duplicatedEmails ||
      validationResult.duplicatedEmails.length === 0
    ) {
      return null;
    }

    const conflictColumns = [
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: 200,
      },
      {
        title: "Usuario Nuevo",
        key: "newUser",
        width: 200,
        render: (record: DuplicatedEmail) => (
          <div>
            <div>
              <strong>
                {record.newUser.first_name} {record.newUser.last_name}
              </strong>
            </div>
            <div style={{ color: "var(--bo-text-secondary)" }}>Rol: {record.newUser.role}</div>
          </div>
        ),
      },
      {
        title: "Usuario Existente",
        key: "existingUser",
        width: 200,
        render: (record: DuplicatedEmail) => (
          <div>
            <div>
              <strong>
                {record.existingUser.first_name} {record.existingUser.last_name}
              </strong>
            </div>
            <div style={{ color: "var(--bo-text-secondary)" }}>Rol: {record.existingUser.role}</div>
            <div style={{ color: "var(--bo-text-secondary)" }}>ID: {record.existingUser.id}</div>
          </div>
        ),
      },
      {
        title: "Clases Actuales",
        key: "classrooms",
        render: (record: DuplicatedEmail) => {
          const classrooms = record.existingUser.classrooms || [];
          if (classrooms.length === 0) return "Sin clases";
          return classrooms.map((c) => c.custom_code).join(", ");
        },
      },
      {
        // SCRUM-822: a match in another school can neither be created nor
        // updated here, so the row has to say so instead of looking updatable.
        title: "Acción",
        key: "action",
        width: 150,
        render: (record: DuplicatedEmail) =>
          record.sameSchool ? (
            <Tag color="green">Se actualizará</Tag>
          ) : (
            <Tag color="red">Otra escuela — se omite</Tag>
          ),
      },
    ];

    return (
      <div style={{ marginTop: 16 }}>
        <Title level={4}>
          Conflictos Detectados ({validationResult.duplicatedEmails.length})
        </Title>
        <Alert
          message="Emails Duplicados Encontrados"
          description={buildConflictNotice(
            updatableConflicts.length,
            blockedConflicts.length,
          )}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Table
          columns={conflictColumns}
          dataSource={validationResult.duplicatedEmails}
          rowKey="email"
          pagination={false}
          scroll={{ x: 800 }}
          size="small"
          className="conflicts-table"
        />
      </div>
    );
  };

  // Función para determinar qué botones mostrar según el estado de validación
  const getActionButtons = () => {
    if (
      processingStep !== "conflicts" ||
      !validationResult ||
      !validationResult.hasConflicts
    ) {
      return [];
    }

    const validUsers = validationResult.summary.valid;
    // SCRUM-822: only same-school duplicates are actually updatable.
    const duplicatedUsers = updatableConflicts.length;
    const totalUsers = validUsers + duplicatedUsers;

    // Caso 1: Solo hay usuarios para actualizar (no hay usuarios nuevos válidos)
    if (validUsers === 0 && duplicatedUsers > 0) {
      return [
        <Button
          key="update-only"
          type="primary"
          loading={loading}
          onClick={() => handleBulkUpload(true, true)}
          style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
        >
          Actualizar Existentes ({countUsers(duplicatedUsers)})
        </Button>,
      ];
    }

    // Caso 2: Solo hay usuarios para crear (no hay duplicados)
    if (validUsers > 0 && duplicatedUsers === 0) {
      return [
        <Button
          key="create-only"
          type="primary"
          loading={loading}
          onClick={() => handleBulkUpload(false, true)}
          style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
        >
          Crear Usuarios Nuevos ({countUsers(validUsers)})
        </Button>,
      ];
    }

    // Caso 3: Hay usuarios para crear Y actualizar
    if (validUsers > 0 && duplicatedUsers > 0) {
      return [
        <Button
          key="create-only"
          loading={loading}
          onClick={() => handleBulkUpload(false, true)}
        >
          Crear Solo Nuevos ({countUsers(validUsers)})
        </Button>,
        <Button
          key="update-and-create"
          type="primary"
          loading={loading}
          onClick={() => handleBulkUpload(true, true)}
          style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
        >
          Actualizar Existentes y Crear Nuevos ({countUsers(totalUsers)})
        </Button>,
      ];
    }

    // Caso 4: No hay usuarios para procesar
    return [];
  };

  return (
    <Modal
      title="Carga Masiva de Usuarios"
      open={visible}
      onCancel={handleClose}
      width={1200}
      className="bulk-upload-modal"
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancelar
        </Button>,
        <Button
          key="download"
          icon={<DownloadOutlined />}
          onClick={downloadTemplate}
        >
          Descargar Template
        </Button>,
        // Botón de validación (solo en preview)
        ...(processingStep === "preview" &&
        previewUsers.length > 0 &&
        validCount > 0
          ? [
              <Button
                key="validate"
                type="primary"
                loading={loading}
                onClick={handleValidateUsers}
                style={{ backgroundColor: "#1890ff", borderColor: "#1890ff" }}
              >
                Validar {validCount} Usuario{validCount !== 1 ? "s" : ""}
              </Button>,
            ]
          : []),
        // Botones de acción dinámicos (en conflicts)
        ...getActionButtons(),
        // Botón de procesando (en upload)
        ...(processingStep === "upload"
          ? [
              <Button
                key="uploading"
                type="primary"
                loading={true}
                disabled={true}
              >
                Procesando...
              </Button>,
            ]
          : []),
      ].filter(Boolean)}
    >
      {showCampusPicker && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>Plantel destino</Text>
          <Select
            style={{ width: "100%", marginTop: 8 }}
            aria-label="Plantel destino"
            value={targetCampusId}
            onChange={setTargetCampusId}
            options={campusOptions(campuses)}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Se aplica a las filas que no traigan la columna Plantel. Con "Todos
            los planteles" cada fila tiene que traerla.
          </Text>
        </div>
      )}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Archivo CSV" key="csv">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Alert
              message="Proceso de Carga Masiva"
              description="1. Sube tu archivo CSV con los usuarios. 2. Revisa y corrige errores. 3. Valida usuarios (detecta duplicados). 4. Confirma la creación/actualización."
              type="info"
              showIcon
            />
            <Alert
              message="Formato CSV"
              description="Sube un archivo CSV con las columnas: first_name, last_name, email, role, grades, classroom_codes. IMPORTANTE: Estudiantes solo pueden tener UN grado, no duplicar códigos de clases."
              type="info"
              showIcon
            />
            <Alert
              message="Límite de Usuarios"
              description="El máximo de usuarios a crear por cada archivo es 100. Si necesitas cargar más usuarios, divide el archivo en múltiples cargas."
              type="warning"
              showIcon
            />
            <Alert
              message="Reglas de Validación"
              description="• ESTUDIANTES: Solo pueden tener UN grado • PROFESORES: Pueden tener múltiples grados y clases sin límite • No duplicar códigos de clases • Los grados deben coincidir con las clases seleccionadas"
              type="warning"
              showIcon
            />
            {classes.length > 0 && (
              <Alert
                message="Códigos de Clases Disponibles"
                description={`Códigos disponibles: ${uniqueClassrooms(classes)
                  .map((c) => c.custom_code || c.class_code)
                  .filter(Boolean)
                  .join(", ")}`}
                type="info"
                showIcon
                style={{ marginTop: 8 }}
              />
            )}
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>Seleccionar Archivo CSV</Button>
            </Upload>
          </Space>
        </TabPane>

        <TabPane tab="Google Sheets" key="sheets">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Alert
              message="Proceso de Carga Masiva"
              description="1. Sube el enlace de tu Google Sheets. 2. Revisa y corrige errores. 3. Valida usuarios (detecta duplicados). 4. Confirma la creación/actualización."
              type="info"
              showIcon
            />
            <Alert
              message="Google Sheets"
              description="Pega el enlace de tu Google Sheets. Asegúrate de que el documento sea público. IMPORTANTE: Estudiantes solo pueden tener UN grado, no duplicar códigos."
              type="info"
              showIcon
            />
            <Alert
              message="Límite de Usuarios"
              description="El máximo de usuarios a crear por cada archivo es 100. Si necesitas cargar más usuarios, divide el archivo en múltiples cargas."
              type="warning"
              showIcon
            />
            <Alert
              message="Reglas de Validación"
              description="• ESTUDIANTES: Solo pueden tener UN grado • PROFESORES: Pueden tener múltiples grados y clases sin límite • No duplicar códigos de clases • Los grados deben coincidir con las clases seleccionadas"
              type="warning"
              showIcon
            />
            {classes.length > 0 && (
              <Alert
                message="Códigos de Clases Disponibles"
                description={`Códigos disponibles: ${uniqueClassrooms(classes)
                  .map((c) => c.custom_code || c.class_code)
                  .filter(Boolean)
                  .join(", ")}`}
                type="info"
                showIcon
              />
            )}
            <Row gutter={16}>
              <Col span={18}>
                <Input
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  value={googleSheetUrl}
                  onChange={(e) => setGoogleSheetUrl(e.target.value)}
                  prefix={<LinkOutlined />}
                />
              </Col>
              <Col span={6}>
                <Button
                  type="primary"
                  loading={loading}
                  onClick={handleGoogleSheetLoad}
                  disabled={!googleSheetUrl.trim()}
                >
                  Cargar
                </Button>
              </Col>
            </Row>
          </Space>
        </TabPane>
      </Tabs>

      {previewUsers.length > 0 && (
        <>
          <Divider />

          {/* Barra de progreso para validación y carga */}
          {(processingStep === "validation" || processingStep === "upload") && (
            <div style={{ marginBottom: 16 }}>
              <Progress
                percent={uploadProgress}
                status={progressStatus}
                strokeColor={{
                  "0%": "#108ee9",
                  "100%": "#87d068",
                }}
                format={(percent) => `${percent}%`}
              />
              {progressText && (
                <div
                  style={{
                    marginTop: 8,
                    textAlign: "center",
                    color: progressStatus === "exception" ? "#ff4d4f" : "var(--bo-text-secondary)",
                    fontWeight: 500,
                  }}
                >
                  {progressText}
                </div>
              )}
            </div>
          )}

          <Title level={4}>
            Vista Previa ({validCount} válidos, {errorCount} con errores)
          </Title>
          {errorCount > 0 && (
            <Alert
              message={`${errorCount} usuario${
                errorCount !== 1 ? "s" : ""
              } con errores`}
              description="Revisa los errores en la tabla y corrige el archivo antes de validar."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          {processingStep === "validation" && (
            <Alert
              message="Validando usuarios..."
              description="Verificando emails duplicados y validaciones del backend."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Mostrar resultado de validación */}
          {validationResult && (
            <Alert
              message="Validación Completada"
              description={buildValidationSummaryText(validationResult)}
              type={validationResult.hasConflicts ? "warning" : "success"}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Table
            columns={previewColumns}
            dataSource={previewUsers}
            rowKey="_index"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1300, y: 400 }}
            size="small"
            rowClassName={(record) =>
              record._status === "error" ? "ant-table-row-error" : ""
            }
          />

          {/* Mostrar tabla de conflictos cuando sea necesario */}
          {validationResult && validationResult.hasConflicts && (
            <ConflictsTable />
          )}
        </>
      )}
    </Modal>
  );
};

export default BulkUploadModal;
